import React, { useEffect, useState, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "utils/AuthContext";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import Button from "components/Button";
import ButtonTiny from "components/ButtonTiny";
import Modal from "components/Modal";
import Input from "components/Input";
import useKey from "utils/KeyListener";
import { ProfilePlaceholder, LogoImage, MenuItem, Nav, NavLeft, NavLink, NavRight, ProfileImage, IconText, Separator } from "./style";
import { Flex, FlexRightCol, FlexCenterCol } from "styles/global-style";

import logo from "imgs/logo_white.png";
import work from "imgs/cards.png";
import people from "imgs/people.png";
import cog from "imgs/cog.png";
import signout from "imgs/logout.png";
import gamepad from "imgs/gamepad.png";
import building from "imgs/building.png";
import arrow from "imgs/ellipse-left.png";
import branch from "imgs/branch.png";

const getEnabledFromSetting = (settings, settingName, defaultValue = true) => {
  const rawValue = settings?.find((s) => s.name === settingName)?.value;
  if (rawValue === undefined || rawValue === null) return defaultValue;
  return rawValue === true || rawValue === "true";
};

export default function AuthMainMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isTrial, logout, updateUser, supabase } = useContext(AuthContext);

  const [hideButtons, setHideButtons] = useState(location.pathname.includes("orgproj"));
  const [showOrgs, setShowOrgs] = useState();
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [addingProject, setAddingProject] = useState(false);
  const [error, setError] = useState("");
  const workTabEnabled = !isTrial && getEnabledFromSetting(user?.proj?.settings, "workTabEnabled");
  const projectTabEnabled = !isTrial && getEnabledFromSetting(user?.proj?.settings, "projectTabEnabled");
  // With no sibling tabs the Timeline button is redundant, the org logo links home instead.
  // Without a logo it has to stay, otherwise there is no way back to the timeline.
  const timelineTabVisible = workTabEnabled || projectTabEnabled || !user?.orgLogo;

  // Keyboard navigation for main menus (1-4 keys)
  useKey("1", false, () => {
    // Only navigate if not in a modal, input field, or textarea
    if (!showOrgs && !showAddProject && !document.activeElement?.matches('input, textarea, [contenteditable]')) {
      navigate("/auth/timeline");
    }
  }, null);

  useKey("2", false, () => {
    if (!showOrgs && !showAddProject && !document.activeElement?.matches('input, textarea, [contenteditable]')) {
      if (!workTabEnabled) return;
      navigate("/auth/work");
    }
  }, null);

  useKey("3", false, () => {
    if (!showOrgs && !showAddProject && !document.activeElement?.matches('input, textarea, [contenteditable]')) {
      if (!projectTabEnabled) return;
      navigate("/auth/project");
    }
  }, null);

  useKey("4", false, () => {
    if (!showOrgs && !showAddProject && !document.activeElement?.matches('input, textarea, [contenteditable]')) {
      navigate("/auth/tree");
    }
  }, null);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const setOrg = useCallback(
    async (org) => {
      if (showOrgs || user.memberships.length === 1) {
        if (org.id !== user.org.id) {
          // User switching org, figure out to which project
          let projectId = null;
          const membership = user.memberships.find((mem) => mem.org_id === org.id);
          if (membership?.project_invite !== null) {
            projectId = membership.project_invite;
            await supabase.from("memberships").update({ project_invite: null }).eq("id", membership.id);
          } else {
            const { data } = await supabase.from("projects").select("id").eq("org_id", org.id).order("id", { ascending: false }).limit(1);
            projectId = data[0].id;
          }
          updateUser({ current_org_id: org.id, current_proj_id: projectId });
        }
        setShowOrgs(false);
      } else {
        setShowOrgs(true);
      }
    },
    [showOrgs, setShowOrgs, user, updateUser, supabase]
  );

  const setProject = useCallback(
    (proj) => {
      if (proj.id !== user.proj.id) {
        updateUser({ current_proj_id: proj.id });
      }
    },
    [updateUser, user]
  );

  // Add project logic
  const handleAddProject = useCallback(async () => {
    if (!newProjectName.trim()) return;
    setAddingProject(true);
    setError("");
    try {
      const res = await supabase.from("projects").insert({ name: newProjectName.trim(), org_id: user.org.id }).select();
      if (res.error) throw new Error(res.error.message);
      // Create default 'unplanned' goal for the new project (only if one doesn't exist)
      const { data: existingUnplanned } = await supabase
        .from("goals")
        .select("id")
        .eq("project_id", res.data[0].id)
        .eq("type", "unplanned")
        .single();
      
      let unplannedGoal;
      if (!existingUnplanned) {
        const { data: newUnplannedGoal } = await supabase.from("goals").insert({ project_id: res.data[0].id, type: "unplanned" }).select().single();
        unplannedGoal = newUnplannedGoal;
      } else {
        unplannedGoal = existingUnplanned;
      }
      
      // Create default story for the unplanned goal
      if (unplannedGoal) {
        const { data: defaultStory } = await supabase
          .from("work")
          .insert({
            title: "Sprint/Story #1 of " + newProjectName.trim(),
            goal_id: unplannedGoal.id,
            project_id: res.data[0].id,
            parent_id: null,
            type: 'story',
            status: null, // stories don't have status
            child_order: [],
            created_by: user.id
          })
          .select()
          .single();

        if (defaultStory) {
          // Update the goal with workitems_order
          await supabase
            .from("goals")
            .update({ workitems_order: [defaultStory.id] })
            .eq("id", unplannedGoal.id);
        }
      }
      
      setShowAddProject(false);
      setNewProjectName("");
      if (typeof updateUser === "function") updateUser({}, null);
      await supabase.from("days").insert({
        person_id: user.id,
        date: new Date().toISOString().slice(0, 10),
        system_type: "joins",
        project_id: res.data[0].id
      });
    } catch (e) {
      setError(e.message || "Could not create project");
    } finally {
      setAddingProject(false);
    }
  }, [newProjectName, supabase, user, updateUser]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useEffect(() => {
    setHideButtons(location.pathname.includes("orgproj"));
  }, [location.pathname, setHideButtons]);

  return (
    <Nav>
      <NavLeft>
        {location.pathname.includes("orgproj") && (
          <NavLink to="/">
            <LogoImage alt="logo" src={logo} />
          </NavLink>
        )}
        {!hideButtons && (
          <>
            {user.orgLogo && (
              <NavLink to="/auth/timeline" style={{ display: 'flex', alignItems: 'center', marginRight: 16 }}>
                <img 
                  src={user.orgLogo} 
                  alt="Organization logo" 
                  style={{ 
                    height: 26, // 80% of 32px button height
                    width: 'auto', 
                    objectFit: 'contain',
                    cursor: 'pointer'
                  }} 
                />
              </NavLink>
            )}
            {timelineTabVisible && <Button text="Timeline" click="/auth/timeline" active={location.pathname.includes("timeline")} noborder image={people} />}
            {workTabEnabled && <Button text="Work" click="/auth/work" active={location.pathname.includes("work")} noborder image={work} />}
            {projectTabEnabled && <Button text="Project" click="/auth/project" active={location.pathname.includes("project")} noborder image={branch} imgSize={13} />}
          </>
        )}
      </NavLeft>

      <NavRight gap={8}>
        {isTrial ? (
          <Flex gap={10} style={{ alignItems: "center" }}>
            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>Trial</span>
            <Button text="Sign up to save" callback={() => navigate("/join")} color="#27ae60" />
            <Button text="Log in" callback={() => navigate("/login")} noborder />
          </Flex>
        ) : (
        <Popover className="popover">
          <PopoverButton className="popover-button">
            <Flex gap={10}>
              <FlexRightCol gap={1} style={{ marginTop: 2 }}>
                <div style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: -1 }}>{user?.org?.name}</div>
                <div style={{ fontSize: "0.7rem" }}>{user?.proj?.name}</div>
              </FlexRightCol>
              {user.avatar ? <ProfileImage src={user.avatar} /> : <ProfilePlaceholder>{user?.email?.substring(0, 1)}</ProfilePlaceholder>}
            </Flex>
          </PopoverButton>

          {!location.pathname.includes("orgproj") && (
            <PopoverPanel className="popover-panel" transition anchor={{ to: "bottom start", padding: "34px" }}>
              {({ close }) => (
                <>
                  <MenuItem click={() => navigate("/auth/settings/account")} close={close}>
                    {user && user.firstname ? user.firstname + (user.lastname ? " " + user.lastname : "") : user.email ? user.email : "Account"}
                    {user && user.role ? <div style={{ color: "#aaa", fontSize: 12, marginTop: -8 }}>{user.role}</div> : ""}
                  </MenuItem>

                  <div style={{ color: "#aaa", fontSize: 12, fontWeight: "bold", margin: "5px 10px", marginBottom: 0 }}>Companies</div>
                  {user.memberships.map((mem, i) => {
                    const currOrg = mem.org_id === user.org.id;
                    if (!showOrgs && !currOrg) return <div key={"org" + i} style={{ visibility: "hidden" }} />;
                    return (
                      <MenuItem key={"org" + i} click={() => setOrg(mem.orgs)}>
                        <Flex style={{ whiteSpace: "nowrap" }}>
                          <IconText icon={building} text={mem.orgs.name} active={showOrgs && currOrg} />
                          {currOrg && !showOrgs && user.memberships.length > 1 ? <ExpandIcon /> : <div style={{ width: 4 }} />}
                        </Flex>
                      </MenuItem>
                    );
                  })}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 10px', marginBottom: 0 }}>
                    <span style={{ color: '#aaa', fontSize: 12, fontWeight: 'bold' }}>Projects</span>
                    <ButtonTiny plus callback={() => setShowAddProject(true)} light />
                  </div>
                  {user.projects &&
                    user.projects.map((val, j) => {
                      return (
                        <MenuItem key={"project" + j} click={() => { setProject(val); close(); }}>
                          <IconText icon={gamepad} text={val.name} active={val.id === user.proj.id} />
                        </MenuItem>
                      );
                    })}

                  {user.projects.length === 0 && <MenuItem>-</MenuItem>}

                  <Separator noline />

                  <MenuItem click={() => navigate("/auth/settings/")} close={close}>
                    <IconText icon={cog} text="Settings" />
                  </MenuItem>

                  <MenuItem click={logout} close={close}>
                    <IconText icon={signout} text="Sign out" />
                  </MenuItem>

                  {/* Modal for adding project */}
                  {showAddProject && (
                    <Modal title="Add Project" closeCb={() => setShowAddProject(false)}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 300 }}>
                        <Input
                          placeholder="Project name"
                          value={newProjectName}
                          onChange={e => setNewProjectName(e.target.value)}
                          autoFocus
                        />
                        {error && <div style={{ color: "red", fontSize: 12 }}>{error}</div>}
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                          <Button text="CANCEL" callback={() => setShowAddProject(false)} disabled={addingProject} lightmode color="#888" />
                          <Button text="ADD" callback={handleAddProject} disabled={!newProjectName.trim() || addingProject} active lightmode color="#27ae60" />
                        </div>
                      </div>
                    </Modal>
                  )}
                </>
              )}
            </PopoverPanel>
          )}
        </Popover>
        )}
      </NavRight>
    </Nav>
  );
}

const ExpandIcon = () => {
  return (
    <FlexCenterCol gap={0}>
      <img alt="arrow-up" src={arrow} style={{ width: 4, opacity: 0.3, transform: "rotate(90deg)" }} />
      <img alt="arrow-up" src={arrow} style={{ width: 4, opacity: 0.3, transform: "rotate(-90deg)" }} />
    </FlexCenterCol>
  );
};
